import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import { Heart, Share2, Mail, ShoppingCart, Pencil } from "lucide-react";

// Helper function to remove "Mini" from product names
const removeMinifromName = (name) => {
  if (!name) return '';
  return name.replace(/\bMini\b\s*/gi, '').trim();
};

export const ProductCard = ({ product, hideActions = false, darkMode = false, compactMode = false }) => {
  const { addToCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const { formatPrice } = useCurrency();
  const { t, translateText, language } = useLanguage();
  const { isAdmin } = useAuth();
  const [imageError, setImageError] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [translatedName, setTranslatedName] = useState(removeMinifromName(product.name));

  // Translate product name when language changes
  useEffect(() => {
    let isMounted = true;
    
    const translateProductName = async () => {
      const cleanName = removeMinifromName(product.name);
      if (language === 'en') {
        setTranslatedName(cleanName);
        return;
      }
      
      try {
        const translated = await translateText(cleanName);
        if (isMounted) {
          setTranslatedName(translated);
        }
      } catch (error) {
        console.error('Translation error:', error);
        if (isMounted) {
          setTranslatedName(cleanName);
        }
      }
    };
    
    translateProductName();
    
    return () => {
      isMounted = false;
    };
  }, [product.name, language, translateText]);

  const productUrl = `${window.location.origin}/product/${product.id}`;
  const productName = encodeURIComponent(translatedName);
  const productDescription = encodeURIComponent(`Check out ${translatedName} at Mini Stripes UK - ${formatPrice(product.price)}`);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const result = await addToCart(product.id, 1);
    if (result.success) {
      toast.success("Added to cart", {
        description: translatedName
      });
    } else {
      toast.error("Failed to add to cart");
    }
  };

  const handleWishlistToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isInWishlist(product.id)) {
      await removeFromWishlist(product.id);
      toast.success("Removed from wishlist");
    } else {
      await addToWishlist(product);
      toast.success("Added to wishlist");
    }
  };

  const handleShare = (e, platform) => {
    e.preventDefault();
    e.stopPropagation();
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(productUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${productDescription}&url=${encodeURIComponent(productUrl)}`,
      pinterest: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(productUrl)}&description=${productDescription}`,
      whatsapp: `https://wa.me/?text=${productDescription}%20${encodeURIComponent(productUrl)}`,
    };
    
    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
    setShowShareMenu(false);
  };

  const handleEmailFriend = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const subject = encodeURIComponent(`Check out this product from Mini Stripes UK`);
    const body = encodeURIComponent(`Hi!\n\nI thought you might like this product:\n\n${translatedName}\nPrice: ${formatPrice(product.price)}\n\nView it here: ${productUrl}\n\nBest regards`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    setShowShareMenu(false);
  };

  // Check if image is a valid product-specific image (not a category placeholder)
  const isProductImage = (url) => {
    if (!url) return false;
    const categoryPlaceholderPatterns = [
      'classic-mini-1959-1986_',
      'mini-cooper-gen-1-2002-2006_',
      'mini-cooper-gen-2-2007-2013_', 
      'mini-cooper-gen-3-2014-early-2024_',
      'mini-cooper-gen-4-2024-onwards_',
      'mini-gp-gen-1-gen-2-gen-3_',
      'mini-countryman-all-generations_',
      'mini-clubman-all-generations_'
    ];
    const lowerUrl = url.toLowerCase();
    return !categoryPlaceholderPatterns.some(pattern => lowerUrl.includes(pattern));
  };

  const hasValidImage = product.image_url && !imageError && isProductImage(product.image_url);

  // Compact mode - show only image, expand on hover
  if (compactMode) {
    return (
      <div className="group product-card relative flex flex-col rounded-xl transition-all duration-300">
        <Link
          to={`/product/${product.id}`}
          className="block"
          data-testid={`product-card-${product.id}`}
        >
          <div className="relative overflow-hidden bg-white aspect-square cursor-pointer rounded-lg shadow-sm group-hover:shadow-xl transition-all duration-300">
            {hasValidImage ? (
              <div className="w-full h-full bg-white overflow-hidden">
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                  onError={() => setImageError(true)}
                />
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100">
                <span 
                  className="text-gray-500 text-lg font-medium tracking-wide"
                  style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
                >
                  {t('awaitingImage')}
                </span>
              </div>
            )}
            
            {/* Admin Edit Button - Top Right (compact mode) */}
            {isAdmin && (
              <Link
                to={`/admin?tab=products&edit=${product.id}`}
                onClick={(e) => e.stopPropagation()}
                className="absolute top-2 right-2 w-7 h-7 bg-blue-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-blue-700 flex items-center justify-center shadow-lg z-20"
                data-testid={`edit-product-compact-${product.id}`}
                title="Edit Product"
              >
                <Pencil className="h-3.5 w-3.5" strokeWidth={2.5} />
              </Link>
            )}
            
            {/* Hover overlay with product info */}
            <div className="absolute inset-0 bg-black/80 opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-end p-4 rounded-lg">
              <h3 
                className="font-semibold text-white text-sm leading-snug mb-2"
                style={{ fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
              >
                {translatedName}
              </h3>
              <p className="text-gray-200 font-bold text-lg">
                {formatPrice(product.price)}
              </p>
              <button
                onClick={handleAddToCart}
                className="mt-3 w-full py-2 bg-white text-black rounded-full font-semibold text-sm hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                data-testid={`add-to-cart-${product.id}`}
              >
                <ShoppingCart className="h-4 w-4" strokeWidth={2.5} />
                Add to Cart
              </button>
            </div>
          </div>
        </Link>
      </div>
    );
  }

  return (
    <div className={`group product-card relative flex flex-col h-full rounded-xl transition-all duration-300 hover:shadow-lg hover:border hover:border-gray-200 p-2 ${darkMode ? 'hover:bg-gray-900' : 'hover:bg-white'}`}>
      <Link
        to={`/product/${product.id}`}
        className="block flex-1 flex flex-col"
        data-testid={`product-card-${product.id}`}
      >
        <div className="relative overflow-hidden bg-white aspect-square cursor-pointer rounded-lg shadow-sm hover:shadow-xl transition-shadow duration-300">
          {hasValidImage ? (
            <div className="w-full h-full bg-white overflow-hidden">
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                onError={() => setImageError(true)}
              />
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100">
              <span 
                className="text-gray-500 text-lg font-medium tracking-wide"
                style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
              >
                {t('awaitingImage')}
              </span>
            </div>
          )}
          
          {/* Admin Edit Button - Top Right */}
          {isAdmin && (
            <Link
              to={`/admin?tab=products&edit=${product.id}`}
              onClick={(e) => e.stopPropagation()}
              className="absolute top-2 right-2 w-8 h-8 bg-blue-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-blue-700 flex items-center justify-center shadow-lg z-10"
              data-testid={`edit-product-${product.id}`}
              title="Edit Product"
            >
              <Pencil className="h-4 w-4" strokeWidth={2.5} />
            </Link>
          )}
          
          <button
            onClick={handleAddToCart}
            className="absolute bottom-4 left-4 w-12 h-12 bg-black text-white rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-gray-800 flex items-center justify-center shadow-xl"
            data-testid={`add-to-cart-${product.id}`}
            title={t('addToCart')}
          >
            <ShoppingCart className="h-6 w-6" strokeWidth={2.5} />
          </button>
        </div>
      </Link>
      
      {/* Action Buttons - Wishlist, Share (includes Email) */}
      {!hideActions && (
        <div className="flex items-center justify-start gap-3 mt-auto pt-4 border-t border-gray-100">
          {/* Wishlist Button - Grey Pill */}
          <button
            onClick={handleWishlistToggle}
            className={`flex items-center gap-2 px-4 py-2 text-xs rounded-full transition-all h-9 border ${
              isInWishlist(product.id) 
                ? 'bg-red-500 text-white border-red-500' 
                : 'bg-secondary text-gray-700 hover:bg-gray-200 border-gray-300'
            }`}
          data-testid={`wishlist-${product.id}`}
        >
          <Heart className={`h-4 w-4 ${isInWishlist(product.id) ? 'fill-white' : ''}`} />
          <span>{t('wishlist')}</span>
        </button>
        
        {/* Share Button with Dropdown (includes Email) - Grey Pill */}
        <div className="relative">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowShareMenu(!showShareMenu);
            }}
            className="flex items-center gap-2 px-4 py-2 text-xs bg-secondary text-gray-700 rounded-full hover:bg-gray-200 transition-all h-9 border border-gray-300"
            data-testid={`share-${product.id}`}
          >
            <Share2 className="h-4 w-4" />
            <span>{t('share')}</span>
          </button>
          
          {/* Share Dropdown Menu */}
          {showShareMenu && (
            <div 
              className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[150px] py-1"
              onMouseLeave={() => setShowShareMenu(false)}
            >
              <button
                onClick={handleEmailFriend}
                className="flex items-center gap-2 w-full px-3 py-2.5 text-xs text-gray-700 hover:bg-gray-50"
              >
                <Mail className="h-4 w-4 text-gray-500" />
                Email a Friend
              </button>
              <div className="border-t border-gray-100 my-1"></div>
              <button
                onClick={(e) => handleShare(e, 'facebook')}
                className="flex items-center gap-2 w-full px-3 py-2.5 text-xs text-gray-700 hover:bg-gray-50"
              >
                <svg className="h-4 w-4 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Facebook
              </button>
              <button
                onClick={(e) => handleShare(e, 'twitter')}
                className="flex items-center gap-2 w-full px-3 py-2.5 text-xs text-gray-700 hover:bg-gray-50"
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                X (Twitter)
              </button>
              <button
                onClick={(e) => handleShare(e, 'pinterest')}
                className="flex items-center gap-2 w-full px-3 py-2.5 text-xs text-gray-700 hover:bg-gray-50"
              >
                <svg className="h-4 w-4 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
                </svg>
                Pinterest
              </button>
              <button
                onClick={(e) => handleShare(e, 'whatsapp')}
                className="flex items-center gap-2 w-full px-3 py-2.5 text-xs text-gray-700 hover:bg-gray-50"
              >
                <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp
              </button>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

export default ProductCard;
